import { useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { useAddOvertime } from '../../lib/queries';
import { useFormErrors } from '../../lib/useFormErrors';
import { todayIso, formatHours } from '../../lib/dates';
import type { Employee } from '../../lib/types';

interface Props {
  employees: Employee[];
  defaultEmployeeId?: number;
  onClose: () => void;
}

/** Buchung aufs Überstundenkonto — positiv aufgebaut, negativ abgebaut. */
export function OvertimeDialog({ employees, defaultEmployeeId, onClose }: Props) {
  const toast = useToast();
  const add = useAddOvertime();
  const { errors, capture, clear, fieldError } = useFormErrors();

  const [employeeId, setEmployeeId] = useState<number | ''>(
    defaultEmployeeId ?? employees[0]?.id ?? '',
  );
  const [date, setDate] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');

  const parsedHours = Number(hours.replace(',', '.'));
  const valid = employeeId !== '' && hours !== '' && Number.isFinite(parsedHours) && parsedHours !== 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();
    if (!valid) return;

    add.mutate(
      { employeeId: employeeId as number, date, hours: parsedHours, reason },
      {
        onSuccess: () => {
          toast.success(`${formatHours(parsedHours)} gebucht.`);
          onClose();
        },
        onError: capture,
      },
    );
  };

  return (
    <Modal
      title="Überstunden buchen"
      subtitle="Positive Stunden bauen auf, negative bauen ab"
      onClose={onClose}
      width="schmal"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={add.isPending}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="overtime-form"
            className="btn btn-signal"
            disabled={add.isPending || !valid}
          >
            {add.isPending ? 'Wird gebucht …' : 'Buchen'}
          </button>
        </>
      }
    >
      <form id="overtime-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        <Field label="Mitarbeiter" error={fieldError('employeeId')}>
          {(props) => (
            <select
              {...props}
              className="select"
              value={employeeId}
              onChange={(event) => setEmployeeId(Number(event.target.value))}
            >
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.displayName}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Datum" error={fieldError('date')}>
          {(props) => (
            <input
              {...props}
              className="input"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Stunden"
          error={fieldError('hours')}
          hint="z. B. 2,5 für aufgebaute oder -8 für abgebaute Stunden"
        >
          {(props) => (
            <input
              {...props}
              className="input"
              inputMode="decimal"
              placeholder="2,5"
              value={hours}
              onChange={(event) => setHours(event.target.value)}
            />
          )}
        </Field>

        <Field label="Grund" error={fieldError('reason')}>
          {(props) => (
            <input
              {...props}
              className="input"
              placeholder="z. B. Samstagseinsatz Schulstraße"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
